import React, {
  AxiosError,
} from 'axios';
import {
  Component,
  PropsWithChildren,
} from 'react';
import isNil from 'lodash/isNil';
import mixpanel from 'mixpanel-browser';
import Button from 'pages/Dashboard/components/Button';
import PopulateTextLogo from 'core/icons/populateLogo';
import {
  isProdOrQA,
} from 'env';

type ErrorBoundaryState = {
  error: unknown;
};

type Props = PropsWithChildren & {
  onLogout?: () => void;
};

export default class ErrorBoundary extends Component<Props, ErrorBoundaryState> {
  constructor(props: Props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error) {
    try {
      console.error('App crashed:', error);
      if (isProdOrQA) {
        mixpanel.track('appCrashed', { message: error?.message });
      }
    } catch (error) {
      console.error('Error tracking app crash:', error);
    }
  }

  render() {
    const { children, onLogout } = this.props;
    const { error } = this.state;
    const hasError = !isNil(error);
    const hasLogout = !isNil(onLogout);

    const axiosError = error as AxiosError;

    const resourceDoesntExist = axiosError?.response?.status === 410;
    const message = resourceDoesntExist
      ? axiosError?.response?.data as string
      : 'Oops, something went wrong.';

    if (hasError) {
      return (
        <div className="relative bg-gray-900 text-white h-[100vh]">
          <div className="absolute">
            <PopulateTextLogo className="!w-full ml-4 mt-4 mr-auto" />
          </div>
          <div className="flex flex-col space-y-10 justify-center items-center h-full">
            <div className="rounded-lg px-12 py-8 max-w-xl mb-20">
              <div className="flex flex-col items-center">
                <h1 className="text-3xl font-semibold">
                  {message}
                </h1>
                {hasLogout && (
                  <p className="text-sm text-gray-300 mt-2">
                    You can try to logout and login again.
                  </p>
                )}
              </div>
              <div className="flex items-center justify-center gap-4 mt-10">
                <Button
                  variant="contained"
                  color="primary"
                  type="button"
                  onClick={() => {
                    if (resourceDoesntExist) {
                      window.location.replace('/encounters');
                    } else {
                      window.location.reload();
                    }
                  }}
                  className="!text-sm !px-4 !py-2"
                >
                  {resourceDoesntExist ? 'Home Screen' : 'Restart App'}
                </Button>
                {hasLogout && (
                  <Button
                    variant="outlined"
                    color="primary"
                    type="button"
                    onClick={() => {
                      onLogout?.();
                    }}
                    className="!text-sm !px-4 !py-2 !text-gray-300 !border-gray-700"
                  >
                    Logout
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>
      );
    }

    return children;
  }
}
